import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://www.myc4p.com';
const LOGO_URL =
  '/logos/c4p-transparent.png';
const DEFAULT_OG =
  '/logos/c4p-transparent.png';

/**
 * SEO Component — React Helmet Async
 *
 * Outputs:
 *   - <title>, meta description, keywords, canonical
 *   - Open Graph (og:*) and Twitter Card meta tags
 *   - JSON-LD @graph with:
 *       - Organization (always present on every page)
 *       - WebPage (always present on every page)
 *       - Any additional schemas passed via `schemas` prop
 *
 * @param {string}   title          Page-specific title (appended with "| Cloud4Partners")
 * @param {string}   description    Meta description 140–160 chars, no pricing
 * @param {string}   keywords       Comma-separated keywords
 * @param {string}   ogImage        Override Open Graph image URL
 * @param {string}   canonical      Canonical path e.g. "/c4p-standard"
 * @param {string}   type           OG type: "website" | "article" (default "website")
 * @param {Array}    schemas        Additional JSON-LD schema objects (Service, FAQPage, etc.)
 * @param {object}   structuredData Legacy single-schema prop (backward compat)
 */
const SEO = ({
  title,
  description,
  keywords = '',
  ogImage = DEFAULT_OG,
  canonical = '',
  type = 'website',
  schemas = [],
  structuredData = null,
}) => {
  const fullTitle = title
    ? `${title} | Cloud4Partners`
    : 'Cloud4Partners | The SAP Business One Partner Platform';

  const canonicalUrl = canonical
    ? `${BASE_URL}${canonical}`
    : BASE_URL;

  // Organization — present on every page
  const orgSchema = {
    '@type': 'Organization',
    '@id': `${BASE_URL}/#organization`,
    name: 'Cloud4Partners',
    url: BASE_URL,
    logo: {
      '@type': 'ImageObject',
      url: LOGO_URL,
      width: 400,
      height: 400,
    },
    description:
      'The complete SAP Business One partner platform. Bundled, standardised, and built on the latest AWS technology. SAP Premier Partner and AWS Advanced Partner.',
    sameAs: ['https://www.linkedin.com/company/cloud4partners/'],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+44-1789-777-466',
      contactType: 'customer service',
      email: 'info@myc4p.com',
      areaServed: 'Worldwide',
    },
  };

  // WebPage — present on every page
  const webPageSchema = {
    '@type': 'WebPage',
    '@id': `${canonicalUrl}#webpage`,
    url: canonicalUrl,
    name: fullTitle,
    ...(description && { description }),
    isPartOf: { '@id': `${BASE_URL}/#website` },
    about: { '@id': `${BASE_URL}/#organization` },
    inLanguage: 'en',
  };

  // Merge: legacy structuredData prop → treat as single additional schema
  const extra = structuredData ? [structuredData] : schemas;
  const graph = [orgSchema, webPageSchema, ...extra].filter(Boolean);

  return (
    <Helmet>
      {/* Primary Meta */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title || 'Cloud4Partners'} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Cloud4Partners" />
      <meta property="og:locale" content="en_GB" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title || 'Cloud4Partners'} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD @graph */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@graph': graph,
        })}
      </script>
    </Helmet>
  );
};

export default SEO;
